import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import { 
  Business, 
  Domain,
  Group,
  ArrowForward
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function UserCompaniesAndOrganizationsCard({ userId }) {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserCompaniesAndOrganizations();
  }, [userId]);

  const fetchUserCompaniesAndOrganizations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchUserCompaniesAndOrganizations(userId);
      
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error);
        showError(`Failed to load user companies and organizations: ${result.error}`, 'Error');
      }
    } catch (err) {
      setError('Failed to load user companies and organizations');
      showError('Failed to load user companies and organizations. Please try again.', 'Error');
      console.error('Failed to fetch user companies and organizations:', err);
    }
    
    setLoading(false);
  };

  const handleCompanyClick = (companyId) => {
    navigate(`/company/${companyId}`);
  };

  const handleOrganizationClick = (organizationId) => {
    navigate(`/project/${organizationId}`);
  };

  const getRoleChipColor = (role) => {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'super_admin':
        return 'error';
      case 'owner':
        return 'warning';
      case 'member':
        return 'info';
      default:
        return 'default';
    }
  };

  const groupOrganizationsByCompany = () => {
    if (!data) return [];
    
    const grouped = {};
    
    // First, add all companies that have organizations
    if (data.organizations) {
      data.organizations.forEach(org => {
        const companyId = org.company_id;
        if (!grouped[companyId]) {
          // Find the company data
          const company = data.companies?.find(c => c.unique_id === companyId);
          grouped[companyId] = {
            company: company || { name: 'Unknown Company', unique_id: companyId },
            organizations: []
          };
        }
        grouped[companyId].organizations.push(org);
      });
    }
    
    // Then, add companies that don't have any organizations
    if (data.companies) {
      data.companies.forEach(company => {
        if (!grouped[company.unique_id]) {
          grouped[company.unique_id] = {
            company: company,
            organizations: []
          };
        }
      });
    }
    
    return Object.values(grouped);
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
            Companies & Projects
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '300px' 
          }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
            Companies & Projects
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!data || (!data.companies?.length && !data.organizations?.length)) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
            Companies & Projects
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              This user is not associated with any companies or projects.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const groupedData = groupOrganizationsByCompany();

  if (groupedData.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
            Companies & Projects
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              This user is not associated with any companies or projects.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
          Companies & Projects
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3}>
            {groupedData.map((group, index) => (
              <Grid item xs={12} key={index}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {/* Company Header */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderRadius: 1,
                        px: 1,
                        mx: -1
                      }
                    }}
                    onClick={() => handleCompanyClick(group.company.unique_id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Business sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" fontWeight="600">
                        {group.company.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip 
                        label={`${group.organizations.length} project${group.organizations.length !== 1 ? 's' : ''}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <ArrowForward sx={{ color: 'text.secondary' }} />
                    </Box>
                  </Box>

                  {/* Company Roles */}
                  {group.company.user_roles && group.company.user_roles.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        User roles in this company:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {group.company.user_roles.map((role, roleIndex) => (
                          <Chip
                            key={roleIndex}
                            label={role}
                            color={getRoleChipColor(role)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Organizations */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 2 }}>
                      <Domain sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                      Projects
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {group.organizations.map((org) => (
                        <Grid item xs={12} md={6} lg={4} key={org.unique_id}>
                          <Paper 
                            elevation={1} 
                            sx={{ 
                              p: 2, 
                              height: '100%',
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 2
                              }
                            }}
                            onClick={() => handleOrganizationClick(org.unique_id)}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.9rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '70%'
                              }}>
                                {org.organization_name || 'Unnamed Project'}
                              </Typography>
                              <Chip 
                                label={org.demo ? 'Demo' : 'Live'} 
                                color={org.demo ? 'info' : 'primary'} 
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
                              ID: {org.unique_id}
                            </Typography>

                            {/* Organization Roles */}
                            {org.user_roles && org.user_roles.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {org.user_roles.map((role, roleIndex) => (
                                  <Chip
                                    key={roleIndex}
                                    label={role}
                                    color={getRoleChipColor(role)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                  />
                                ))}
                              </Box>
                            )}

                            {org.industry && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                Industry: {org.industry}
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Summary */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Total: {data.total_companies} companies, {data.total_organizations} projects
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default UserCompaniesAndOrganizationsCard; 